// @flow strict

import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useAuthStore from "../store/authStore";
import useReservationStore from "../store/reservationStore";
import ReservationItem from "../components/ReservationItem";

function ReservationUserPage() {
  const { reservations, isLoading, getReservations } = useReservationStore();
  const { user, getUser } = useAuthStore();

  const [filteredReservation, setFilteredReservation] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchDate, setSearchDate] = useState("");
  const [mode, setMode] = useState("");

  const statusList = ["PENDING", "CONFIRMED", "CANCELLED", "COMPLETED"];
  const STATUS_LABELS = {
    PENDING: "En attente",
    CONFIRMED: "Confirmé",
    CANCELLED: "Annulé",
    COMPLETED: "Terminé",
  };

  // Pour mobile, on montre moins de statuts visibles
  const maxVisible = window.innerWidth < 768 ? 2 : 3;
  let hiddenStatuses = statusList.slice(maxVisible);
  let visibleStatuses = [];
  if (hiddenStatuses.length < 2) {
    visibleStatuses = statusList;
    hiddenStatuses = [];
  } else {
    visibleStatuses = statusList.slice(0, maxVisible);
  }

  // Calculer les totaux
  const calculateTotals = (reservations) => {
    const totalServices = reservations.reduce((total, reservation) => {
      return total + (reservation.appointmentServices?.length || 0);
    }, 0);

    const totalAmount = reservations.reduce((total, reservation) => {
      return total + parseFloat(reservation.totalAmount || 0);
    }, 0);

    return { totalServices, totalAmount };
  };

  const { totalServices, totalAmount } = calculateTotals(filteredReservation);

  useEffect(() => {
    const init = async () => {
      if (!user?.id) await getUser();
      if (user?.id) await getReservations(user.id);
    };
    init();
  }, [user]);

  useEffect(() => {
    let filtered = reservations || [];

    if (mode !== "") {
      filtered = filtered.filter((r) => r?.status === mode);
    }

    if (searchDate) {
      filtered = filtered.filter((r) => {
        if (!r?.date) return false;
        const rdvDate = new Date(r.date).toISOString().split("T")[0];
        return rdvDate === searchDate;
      });
    }

    // Filtrer les réservations valides
    const validReservations = filtered.filter(
      (reservation) => reservation && reservation.id
    );

    setFilteredReservation(validReservations);
  }, [mode, reservations, searchDate]);

  if (isLoading || !user?.id) {
    return (
      <motion.div
        key="loading"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.4 }}
        className="h-screen flex justify-center items-center"
      >
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white mr-1" />
          <span className="font-nunito text-[14px]">Chargement de vos données...</span>
        </div>
      </motion.div>
    );
  }

  document.title = `${user?.role?.name || ""} ${user?.prenom || ""} ${
    user?.nom || ""
  } || Réservation`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-secondary-100 py-4 px-2 sm:px-4">
      <div className="w-full max-w-4xl mx-auto bg-white text-gray-800 shadow-2xl  p-4 sm:p-6 md:p-8 flex flex-col min-h-0">
        {/* En-tête avec totaux */}
        <div className="mb-4 sm:mb-6">
          <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">
            Mes Réservations
          </h1>

          {/* Cartes de totaux */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-secondary-50 p-3 sm:p-4 border border-secondary-200 ">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-poppins font-medium text-[15px] text-secondary-600">
                    Total Services
                  </p>
                  <p className="font-poppins font-bold text-[16px] text-secondary-800">
                    {totalServices}
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-secondary-100 flex items-center justify-center rounded">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-3 sm:p-4 border border-green-200 ">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-poppins font-medium text-[15px] text-green-600">
                    Montant Total
                  </p>
                  <p className="font-poppins font-bold text-[16px] text-green-800">
                    {totalAmount.toFixed(2)} FCFA
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 flex items-center justify-center rounded">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres - Version mobile améliorée */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4 relative w-full">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => setMode("")}
              className={`px-3 py-1.5 font-nunito font-semibold text-[14px] transition-colors duration-200 ${
                mode === ""
                  ? "bg-secondary-700 text-white"
                  : "bg-white border border-gray-300 hover:bg-gray-50"
              }`}
            >
              Tous
            </button>

            {visibleStatuses.map((status) => (
              <button
                key={status}
                onClick={() => setMode(status)}
                className={`px-3 py-1.5 font-nunito font-semibold text-[14px] transition-colors duration-200 ${
                  mode === status
                    ? "bg-secondary-700 text-white"
                    : "bg-white border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {STATUS_LABELS[status] || status}
              </button>
            ))}
          </div>

          {hiddenStatuses.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-3 py-1.5 font-nunito font-semibold text-[14px] bg-white border border-gray-300 hover:bg-gray-50 transition-colors duration-200 rounded"
              >
                Autres ▾
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-1 right-0 bg-white border border-gray-300 shadow-lg z-10 min-w-32 rounded">
                  {hiddenStatuses.map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setMode(status);
                        setDropdownOpen(false);
                      }}
                      className={`block w-full px-3 py-2 text-left font-nunito font-semibold text-[14px] transition-colors duration-200 ${
                        mode === status
                          ? "bg-secondary-700 text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {STATUS_LABELS[status] || status}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtre par date */}
        <div className="flex justify-end mb-3 sm:mb-4">
          <div className="relative w-full sm:w-auto">
            <input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              className="w-full sm:w-auto px-3 py-1.5 font-nunito text-[14px] border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent transition-colors duration-200 rounded"
            />
            {searchDate && (
              <button
                onClick={() => setSearchDate("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Liste des réservations - Scroll corrigé */}
        <motion.div
          key="showFilteredReservation"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
          className="flex-1 min-h-0 bg-gray-50 border  overflow-hidden"
        >
          <div className="h-full overflow-y-auto p-3 sm:p-4 md:p-6">
            <ul className="w-full space-y-3 sm:space-y-4">
              <AnimatePresence mode="wait">
                {filteredReservation.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-gray-500 py-6 sm:py-8 h-full flex flex-col items-center justify-center"
                  >
                    <svg
                      className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <p className="font-poppins font-medium text-[15px] text-gray-400">
                      Aucun rendez-vous trouvé
                    </p>
                    <p className="font-nunito text-[14px] text-gray-500 mt-1">
                      {mode !== "" || searchDate
                        ? "Essayez de modifier vos filtres"
                        : "Vous n'avez aucune réservation pour le moment"}
                    </p>
                  </motion.div>
                ) : (
                  filteredReservation.map((reservation) => (
                    <ReservationItem key={reservation.id} item={reservation} />
                  ))
                )}
              </AnimatePresence>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ReservationUserPage;